import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button, Input, Alert } from '../ui';
import { useCountry } from '../../hooks/useCountry';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Password Strength Meter
const PasswordStrength = ({ password }) => {
  const getStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strength = getStrength();
  const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  const barColors = ['bg-error-500', 'bg-warning-500', 'bg-amber-400', 'bg-success-400', 'bg-success-500'];

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength ? barColors[strength - 1] : 'bg-gray-200'}`} />
        ))}
      </div>
      <p className={`text-xs ${strength <= 2 ? 'text-warning-600' : 'text-success-600'}`}>
        {labels[strength - 1] || 'Very Weak'}
      </p>
    </div>
  );
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { theme, country: countryConfig, isSubdomain } = useCountry();
  const { i18n } = useTranslation();
  const colors = theme?.colors || {};

  const [formData, setFormData] = useState({ new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isSubdomain && countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [isSubdomain, countryConfig, i18n]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (formData.new_password !== formData.confirm_password) { setError('Passwords do not match'); return; }
    if (formData.new_password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/password-reset-confirm/`, { token, new_password: formData.new_password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: theme ? colors.heroBg : undefined }}>
        {!theme && <div className="absolute inset-0 hero-gradient-bg" />}
        <div className="glass-card p-8 max-w-md w-full text-center animate-scale-in relative z-10">
          <div className="w-20 h-20 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h1>
          <p className="text-gray-600 mb-8">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="btn btn-primary inline-flex">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="flex items-center space-x-2 mb-8 group">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))', boxShadow: theme ? `0 10px 25px -5px ${colors.primary}40` : undefined }}>
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className={`text-2xl font-bold ${theme ? '' : 'gradient-text'}`} style={theme ? { color: colors.primary } : undefined}>RentyDoc</span>
          </Link>

          {success ? (
            <div className="text-center animate-scale-in">
              <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <svg className="w-10 h-10 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full animate-bounce" style={{ backgroundColor: colors.primary || 'var(--primary-400)', animationDelay: '0s' }} />
                <div className="absolute -top-1 -right-3 w-3 h-3 rounded-full animate-bounce" style={{ backgroundColor: colors.accent || 'var(--accent-400)', animationDelay: '0.2s' }} />
                <div className="absolute -bottom-2 -right-1 w-4 h-4 bg-success-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Password Reset Successfully!</h1>
              <p className="text-gray-600 mb-8">Your password has been reset. You can now log in with your new password.</p>
              <Link to="/login" className="btn btn-primary w-full flex items-center justify-center">
                Go to Login
                <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Set new password</h1>
                <p className="text-gray-600">Create a new secure password for your account.</p>
              </div>
              {error && <Alert type="error" className="mb-6" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input label="New Password" type="password" name="new_password" value={formData.new_password} onChange={handleChange} required placeholder="Create a strong password" />
                  <PasswordStrength password={formData.new_password} />
                </div>
                <Input label="Confirm New Password" type="password" name="confirm_password" value={formData.confirm_password} onChange={handleChange} required placeholder="Confirm your new password" />
                <Button type="submit" variant="primary" fullWidth size="lg" loading={loading}>Reset Password</Button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden" style={{ background: theme ? colors.heroBg : undefined }}>
        {!theme && <div className="absolute inset-0 hero-gradient-bg" />}
        <div className="absolute w-96 h-96 rounded-full -top-48 -right-48 blur-3xl float-animation" style={{ backgroundColor: theme ? `${colors.primaryLight}40` : 'rgba(var(--primary-400-rgb), 0.3)' }} />
        <div className="absolute w-80 h-80 rounded-full bottom-1/4 -left-40 blur-3xl float-animation-delayed" style={{ backgroundColor: theme ? `${colors.accent || colors.secondary}30` : 'rgba(var(--accent-400-rgb), 0.3)' }} />
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-16">
          <div className="glass-card p-8 max-w-md text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))' }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create a Strong Password</h2>
            <ul className="text-left text-gray-600 space-y-2">
              {['At least 8 characters', 'Mix of letters and numbers', 'Include special characters'].map((item, i) => (
                <li key={i} className="flex items-center">
                  <svg className="w-4 h-4 mr-2" style={{ color: colors.primary || 'var(--primary-500)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
